import { faQrcode } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import { useState } from 'react';

import DetailsNav from '@/modules/league/details/components/DetailsNav';
import ExportQRCodeModal from '@/modules/league/details/components/LeagueQRCodeModal';
import AllGameDaysModal from '@/modules/league/new-components/header/HeaderLeagueButtons/AllGameDaysModal';
import { LeagueDetails, SessionLeagueDetails } from '@/modules/league/new-components/types';
import { ClubMemberData } from '@/types/leagues.types';
import { Session } from '@/types/types';

export interface SidebarBoxWithButtonsProps {
  details: LeagueDetails;
  session: SessionLeagueDetails;
  clubMember: ClubMemberData[];
  isRegistrationActive: boolean;
  params: { slug: string; sessionId: string };
  showSponsors: boolean;
  isAuthorized: boolean;
  isSuperAdmin: boolean;
  user?: Session;
}

export default function SidebarBoxWithButtons({
  details,
  session,
  clubMember,
  isRegistrationActive,
  params,
  showSponsors,
  isAuthorized,
  isSuperAdmin,
  user,
}: SidebarBoxWithButtonsProps) {
  const { slug, sessionId } = params;

  const isUserSessionAvailable = user?.pbUuid && user.token;

  const [showAllGameDays, setShowAllGameDays] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);

  const showBecomeAClubMemberButton = isRegistrationActive && clubMember?.length === 0;
  const contactALeagueURL = `https://contact.${process.env.NEXT_PUBLIC_REWRITE_DOMAIN_NAME}/contacts/league`;

  return (
    <>
      <div className='pt-6 pb-8 sm:py-5 w-full'>
        <div className='flex w-full flex-col justify-between rounded-md text-gray-600 shadow-lg lg:mt-0 lg:max-w-md lg:border lg:p-10'>
          {showBecomeAClubMemberButton ? (
            <a
              href={`${process.env.NEXT_PUBLIC_PC_URL}/clubs/${details.ClubSlug ? details.ClubSlug : details.ClubID}/membership-plans`}
              rel={isUserSessionAvailable ? undefined : 'nofollow'}
              aria-label={`Link to become a ${details.ClubTitle} club member`}
            >
              <Button variant='secondary' className='mb-4 w-full justify-center'>
                Become a club member
              </Button>
            </a>
          ) : null}

          <a href={`${contactALeagueURL}/${details?.LeagueID}`} aria-label='Link to contact league'>
            <Button variant='secondary' className='mb-4 w-full justify-center'>
              Contact League
            </Button>
          </a>

          {isUserSessionAvailable && isAuthorized ? (
            <>
              <Button
                variant='secondary'
                className='mb-4 w-full justify-center'
                prefixIcon={<FontAwesomeIcon icon={faQrcode} size='lg' />}
                onClick={() => {
                  setShowQrCode(true);
                }}
              >
                Export QR as PDF
              </Button>
              <a
                href={`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/a5_${isSuperAdmin ? 'a/l' : 'u/l'}/sesss.aspx?lid=${details?.LeagueID}&uid=&cid=${details?.ClubID}`}
                aria-label={`Link to manage ${details.Title} league`}
              >
                <Button
                  variant='secondary'
                  className='mb-4 w-full justify-center !bg-darkblue-500 !text-white hover:!bg-darkblue-700'
                >
                  Manage League
                </Button>
              </a>
            </>
          ) : null}

          <Button
            variant='primary'
            className='items-center !bg-leagues-300 justify-center'
            onClick={() => {
              setShowAllGameDays(true);
            }}
          >
            All Game Days
          </Button>
        </div>
      </div>

      <div className='lg:hidden overflow-x-scroll pb-4 sm:overflow-hidden max-w-[calc(100vw-2rem)] -mr-4'>
        <DetailsNav
          leagueId={session.LeagueID}
          sessionId={session.LeagueSessionID}
          params={params}
          showSponsors={showSponsors}
        />
      </div>

      {showQrCode ? (
        <ExportQRCodeModal
          isAuthorized={isAuthorized}
          details={details}
          session={session}
          showQrCode={showQrCode}
          setShowQrCode={setShowQrCode}
        />
      ) : null}

      {showAllGameDays ? (
        <AllGameDaysModal
          leagueId={session?.LeagueID}
          sessionId={sessionId}
          slug={slug}
          modalOpened={showAllGameDays}
          setIsModalOpen={setShowAllGameDays}
        />
      ) : null}
    </>
  );
}
